import { memo, useState, useEffect } from "react";
import Close from "@zenith/assets/icons/Close";
import { Button, DialogContent, IconButton } from "@mui/material";
import CitizenFormComponent from "../citizen-form-component/citizen-form-component";
import ForeignerFormComponent from "../foreigner-form-component/foreigner-form-component";
import { Director } from "../interfaces";
import { CitizenForm } from "../citizen-form-component/citizen-form.model";
import { ForeignerForm } from "../foreigner-form-component/foreigner-form.model";
import Dialog from "@mui/material/Dialog";
import { SelectComponent } from "@zenith/components";
import { selectItems } from "../director-details.util";

/* eslint-disable-next-line */
export interface EditDirectorModalComponentProps {
  showModal: boolean;
  onClickClose: (event?: object) => void;
  classNames?: string;
  onContinueButtonClick: (
    director: Director,
    formData: ForeignerForm | CitizenForm
  ) => void;
  director: Director | undefined;
}

const EditDirectorModalComponent = memo(function EditDirectorModalComponent(
  props: EditDirectorModalComponentProps
) {
  const { onClickClose, showModal, onContinueButtonClick, director } = props;
  const [open, setOpen] = useState(showModal);

  useEffect(() => {
    setOpen(showModal);
  }, [showModal]);

  const [updatedCitizenForm, setUpdatedCitizenForm] = useState<
    CitizenForm | undefined
  >(director?.citizenData);
  const [updatedForeignerForm, setUpdatedForeignerForm] = useState<
    ForeignerForm | undefined
  >(director?.foreignerData);
  const [saveChangesDisabled, setSaveChangesDisabled] = useState<boolean>(true);

  const saveCitizenForm = (form: CitizenForm) => {
    setUpdatedCitizenForm(form);
  };
  const saveForeignerForm = (form: ForeignerForm) => {
    setUpdatedForeignerForm(form);
  };

  const isSaveDisabled = () => {
    if (director?.type === "citizen") {
      return (
        JSON.stringify(updatedCitizenForm) ===
        JSON.stringify(director.citizenData)
      );
    }
    if (director?.type === "foreigner") {
      return (
        JSON.stringify(updatedForeignerForm) ===
        JSON.stringify(director.foreignerData)
      );
    }
    return true;
  };

  const setSaveDisabled = (state: boolean) => {
    setSaveChangesDisabled(state);
  };

  const onClickSave = () => {
    if (director?.type === "citizen" && updatedCitizenForm) {
      onContinueButtonClick(director, updatedCitizenForm);
    }
    if (director?.type === "foreigner" && updatedForeignerForm) {
      onContinueButtonClick(director, updatedForeignerForm);
    }
  };

  return (
    <div>
      <Dialog
        open={open}
        aria-labelledby="modal-modal-title"
        onClose={onClickClose}
        maxWidth="md"
        fullWidth={true}
        aria-describedby="modal-modal-description"
        classes={{
          paper: "rounded-3xl",
        }}
      >
        <DialogContent>
          <div className="flex justify-end">
            <IconButton className="mr-5 mt-5 self-end" onClick={onClickClose}>
              <Close />
            </IconButton>
          </div>
          <h4 className="mb-4 text-lg font-bold capitalize">
            {director?.name}
          </h4>
          <SelectComponent
            items={selectItems}
            className="w-auto"
            label="Is the director a foreigner?"
            id="directorsDetails"
            labelId="directorsDetailsLabel"
            value={director?.type}
            defaultValue={""}
            disabled={true}
            placeholder="Select the option"
            onChange={() => void 0}
          />

          <div className="my-5 flex-grow border-t border-neutral-100"></div>
          {director?.type === "citizen" && director.citizenData && (
            <CitizenFormComponent
              setAddDirectorDisabled={setSaveDisabled}
              editFormState={director.citizenData}
              editingMode={true}
              saveCitizenForm={saveCitizenForm}
            />
          )}
          {director?.type === "foreigner" && director.foreignerData && (
            <ForeignerFormComponent
              setAddDirectorDisabled={setSaveDisabled}
              editFormState={director.foreignerData}
              editingMode={true}
              saveForeignerForm={saveForeignerForm}
            />
          )}

          <div className="mb-6 flex w-full gap-4">
            <Button
              id="cancel-director-edit-button"
              className="secondary-button w-full md:w-full"
              size="large"
              onClick={() => onClickClose()}
            >
              Cancel
            </Button>
            <Button
              id="save-director-changes-button"
              className="primary-button w-full md:w-full"
              onClick={onClickSave}
              size="large"
              disabled={saveChangesDisabled || isSaveDisabled()}
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default EditDirectorModalComponent;
